import { api } from '../../lib/api';
import type { NotificationPage } from './types';

/**
 * Notifications transport.
 *
 * ── ASSUMED CONTRACT ──────────────────────────────────────────────────────
 *   GET  /workspaces/:workspaceId/notifications?unreadOnly=&limit=&cursor=  -> NotificationPage
 *   GET  /workspaces/:workspaceId/notifications/count                       -> { unread: number }
 *   POST /workspaces/:workspaceId/notifications/read   { ids?: string[] }   -> { updated: number }
 *   GET    /workspaces/:workspaceId/requests/:requestId/watch               -> Watchers
 *   POST   /workspaces/:workspaceId/requests/:requestId/watch               -> { watching: true }
 *   DELETE /workspaces/:workspaceId/requests/:requestId/watch               -> { watching: false }
 *
 * The server applies the visibility rule in the query on every one of these
 * (see notifications.service.ts's file header) — nothing here re-filters a
 * response that already arrived, same posture as activity/api.ts.
 */

export interface NotificationsQuery {
  readonly unreadOnly?: boolean;
  readonly limit?: number;
  readonly cursor?: string;
}

function toParams(query: NotificationsQuery): string {
  const params = new URLSearchParams();
  if (query.unreadOnly === true) params.set('unreadOnly', 'true');
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.cursor !== undefined) params.set('cursor', query.cursor);
  const qs = params.toString();
  return qs === '' ? '' : `?${qs}`;
}

export function fetchNotifications(
  workspaceId: string,
  query: NotificationsQuery = {},
  signal?: AbortSignal,
): Promise<NotificationPage> {
  return api.get<NotificationPage>(
    `/workspaces/${encodeURIComponent(workspaceId)}/notifications${toParams(query)}`,
    signal,
  );
}

export function fetchUnreadCount(workspaceId: string, signal?: AbortSignal): Promise<{ unread: number }> {
  return api.get<{ unread: number }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/notifications/count`,
    signal,
  );
}

/** Omitting `ids` (or passing `undefined`) marks every unread notification read. */
export function markNotificationsRead(
  workspaceId: string,
  ids?: readonly string[],
): Promise<{ updated: number }> {
  return api.post<{ updated: number }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/notifications/read`,
    ids !== undefined ? { ids } : {},
  );
}

export function watchRequest(workspaceId: string, requestId: string): Promise<{ watching: true }> {
  return api.post<{ watching: true }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/watch`,
  );
}

export function unwatchRequest(workspaceId: string, requestId: string): Promise<{ watching: false }> {
  return api.delete<{ watching: false }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/watch`,
  );
}

// ---------------------------------------------------------------------------
// Following one request — see notifications.service.ts
// ---------------------------------------------------------------------------

/** How someone came to be following. Mirrors `WatchReason` on the server. */
export type WatchReason = 'creator' | 'commenter' | 'mentioned' | 'opted_in' | 'added';

export interface Watcher {
  readonly userId: string;
  readonly name: string;
  readonly reason: WatchReason;
}

export interface Watchers {
  /** Whether the CALLER is following. Resolved server-side, so the UI never
   * has to know its own user id to answer it. */
  readonly watching: boolean;
  readonly items: readonly Watcher[];
}

function watchPath(workspaceId: string, requestId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/watch`;
}

export function fetchWatchers(
  workspaceId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<Watchers> {
  return api.get<Watchers>(watchPath(workspaceId, requestId), signal);
}

export const watchKeys = {
  forRequest: (workspaceId: string, requestId: string) =>
    ['ws', workspaceId, 'requests', requestId, 'watchers'] as const,
};
